import { FC, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import {
  Button,
  ButtonIcon,
  FormInput,
  FormToggle,
  IconChip,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  createCampo,
  createCampoOfEmpresa,
  createFormulario,
  createFormularioOfEmpresa,
  deleteCampos,
  deleteCamposOfEmpresa,
  duplicarFormulario,
  reordenarCampos,
  reordenarCamposOfEmpresa,
  updateFormulario,
  updateFormularioOfEmpresa,
} from '../../core/_requests'
import { Label } from '../../../../../../components'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createValidationSchema, updateValidationSchema } from './validation/formularioSchema'
import {
  ButtonType,
  FontSize,
  IconClass,
  IconPaths,
  IconSize,
  InputType,
  Permission,
  showAlert,
  showConfirmAlert,
  showError,
  showChoiceAlert,
} from '../../../../../../../_metronic/helpers'
import {
  FormularioCampoView,
  FormularioDtoCampoCreate,
  FormularioDtoCreate,
  FormularioDtoUpdate,
  FormularioView,
} from '../../core/_models'
import { FieldConfigModal } from '../inputs/FieldConfigModal'
import { FormularioPreview } from '../modal/FormularioPreview'
import { renderField } from '../inputs/renderField'
import { useAuth } from '../../../../../auth'
import { useT } from '_metronic/i18n/Metronici18n'
import { getCatalogoCondicaoFormulario } from '../../../core/_requests'
import { CATALOGO_PADRAO, CondicaoCatalogo } from '../../../core/condicao/catalogo'
import {
  CampoEsquema,
  Condicao,
  CondicaoAtomica,
  ehGrupo,
  itensDoGrupo,
} from '../../../core/condicao/tipos'
import { tipoCampoCondicaoDe } from '../../../core/condicao/esquema'
import {
  esquemaParaExibicao,
  validarCondicaoLocal,
} from '../../../core/condicao/validarCondicaoLocal'
import { CampoReferenciavel } from '../inputs/condicao/tiposEditor'
import { CODIGO_FORMULARIO_BLOQUEADO, extrairErrosFormulario } from '../../../core/errosFormulario'

type Props = {
  formulario?: FormularioView
  /** Formulário bloqueado por um provedor: o construtor abre só para leitura. */
  somenteLeitura?: boolean
  /** Recarrega o formulário do servidor (usado depois de um 409/ORDEM_INVALIDA). */
  onRecarregar?: () => void
}

const formFields: { name: string; type: InputType; class: IconClass; descricao: string }[] = [
  {
    name: 'Editor de Texto',
    type: InputType.TextEditor,
    class: IconClass.FileText,
    descricao: 'Texto com formatação avançada',
  },
  {
    name: 'Texto',
    type: InputType.Text,
    class: IconClass.TextParagraph,
    descricao: 'Resposta curta em uma linha',
  },
  {
    name: 'Marcar',
    type: InputType.Radio,
    class: IconClass.CheckCircle,
    descricao: 'Escolha única entre alternativas',
  },
  {
    name: 'Número',
    type: InputType.Number,
    class: IconClass.Numeric,
    descricao: 'Aceita apenas valores numéricos',
  },
  {
    name: 'Selecionar',
    type: InputType.Select,
    class: IconClass.ListCheck,
    descricao: 'Lista de opções para escolher',
  },
]

const paraCampoReferenciavel = (campo: FormularioCampoView): CampoReferenciavel => ({
  codigo: campo.codigo ?? '',
  rotulo: campo.rotulo || 'Pergunta sem título',
  tipo: tipoCampoCondicaoDe(campo.tipo),
  multiplo: !!campo.multiplo,
  opcoes: (campo.opcoes ?? [])
    .filter((opcao) => !!opcao.codigo)
    .map((opcao) => ({ codigo: opcao.codigo as string, descricao: opcao.descricao })),
})

const paraCampoEsquema = (campo: FormularioCampoView, indice: number): CampoEsquema => ({
  codigo: campo.codigo ?? '',
  tipo: tipoCampoCondicaoDe(campo.tipo),
  multiplo: !!campo.multiplo,
  ordem: campo.ordem,
  desempate: Number(campo.id ?? Number.MAX_SAFE_INTEGER - indice),
  codigosOpcao: (campo.opcoes ?? [])
    .map((opcao) => opcao.codigo)
    .filter((codigo): codigo is string => !!codigo),
})

/** Códigos de campo citados por uma condição (fonte `campo`). */
const referenciasDeCampo = (condicao: Condicao | null | undefined, destino: Set<string>): void => {
  if (!condicao) return
  if (ehGrupo(condicao)) {
    itensDoGrupo(condicao).forEach((item) => referenciasDeCampo(item, destino))
    return
  }
  const atomica = condicao as CondicaoAtomica
  if (atomica.fonte === 'campo' && atomica.ref) destino.add(atomica.ref)
}

/** Rótulos dos campos cuja condição de exibição referencia `codigo`. */
const camposQueDependemDe = (campos: FormularioCampoView[], codigo?: string): string[] => {
  if (!codigo) return []
  return campos
    .filter((campo) => {
      const refs = new Set<string>()
      referenciasDeCampo(campo.condicaoExibicao, refs)
      return refs.has(codigo)
    })
    .map((campo) => campo.rotulo || campo.codigo || '')
}

const paraDtoCampoCreate = (campo: FormularioCampoView): FormularioDtoCampoCreate => ({
  ordem: campo.ordem,
  rotulo: campo.rotulo,
  instrucoes: campo.instrucoes,
  respostaPadrao: campo.respostaPadrao,
  tipo: campo.tipo,
  obrigatorio: campo.obrigatorio,
  multiplo: campo.multiplo,
  opcoesDetalhadas: (campo.opcoes ?? []).map((opcao, indice) => ({
    ordem: opcao.ordem ?? indice + 1,
    descricao: opcao.descricao,
    ...(opcao.codigo ? { codigo: opcao.codigo } : {}),
  })),
  ...(campo.codigo ? { codigo: campo.codigo } : {}),
  condicaoExibicao: campo.condicaoExibicao ?? null,
})

export const FormularioEdit: FC<Props> = ({
  formulario: form,
  somenteLeitura = false,
  onRecarregar,
}) => {
  const { showModal } = useModal()
  const { currentUser } = useAuth()
  const [searchParams] = useSearchParams()
  const { t } = useT()

  const navigate = useNavigate()

  const [deletedFields, setDeletedFields] = useState<number[]>([])
  const [orderHasChanged, setOrderHasChanged] = useState<boolean>(false)
  const [catalogo, setCatalogo] = useState<CondicaoCatalogo>(CATALOGO_PADRAO)
  /** Avisos por código de campo, vindos da validação local (ex.: referência a campo posterior). */
  const [avisosServidor, setAvisosServidor] = useState<Record<string, string>>({})
  const [duplicando, setDuplicando] = useState<boolean>(false)

  const initialValues = form
    ? form
    : {
        assinar: false,
        descricao: '',
        ativo: true,
        campos: [],
        nome: '',
        assinarPaciente: false,
      }

  const isAdmin = currentUser?.grupo?.nome === Permission.Admin
  const empresaId = Number(searchParams.get('empresaId'))

  useEffect(() => {
    let ativo = true
    getCatalogoCondicaoFormulario()
      .then((resposta) => {
        if (ativo) setCatalogo(resposta)
      })
      .catch(showError)
    return () => {
      ativo = false
    }
  }, [])

  const handleAddField = (
    setFieldValue: (field: string, value: any) => void,
    fields: FormularioCampoView[],
    tipo: InputType
  ) => {
    const nextOrdem = fields.reduce((max, field) => Math.max(max, field.ordem), 0) + 1
    const newField: FormularioCampoView = {
      ordem: nextOrdem,
      rotulo: '',
      instrucoes: '',
      tipo,
      obrigatorio: false,
      multiplo: false,
      respostaPadrao: '',
      condicaoExibicao: null,
      opcoes:
        tipo === InputType.Select || tipo === InputType.Radio
          ? [
              { ordem: 1, descricao: 'Sim', codigo: 'sim' },
              { ordem: 2, descricao: 'Não', codigo: 'nao' },
            ]
          : [],
    }

    setFieldValue('campos', [...fields, newField])
  }

  const handleBloqueado = async (mensagem: string) => {
    await showAlert({ title: t('common.error'), text: mensagem, icon: 'warning' })
    onRecarregar?.()
  }

  const handleEditField = (
    field: FormularioCampoView,
    campos: FormularioCampoView[],
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const anteriores = campos
      .filter((candidato) => candidato.ordem < field.ordem)
      .map(paraCampoReferenciavel)

    showModal({
      content: (
        <FieldConfigModal
          field={field}
          formId={initialValues.id}
          camposAnteriores={anteriores}
          catalogo={catalogo}
          somenteLeitura={somenteLeitura}
          onBloqueado={handleBloqueado}
          onUpdate={(updatedConfig: FormularioCampoView) =>
            handleUpdateField(updatedConfig, campos, setFieldValue)
          }
        />
      ),
      title: 'Configurar campo',
    })
  }

  const handleUpdateField = (
    updatedConfig: FormularioCampoView,
    fields: FormularioCampoView[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updatedFields = fields.map((field) =>
      field.ordem === updatedConfig.ordem ? { ...field, ...updatedConfig } : field
    )

    setFieldValue('campos', updatedFields)
  }

  /**
   * Exclusão de campo: avisa antes quando outra condição referencia o código deste campo (o
   * backend recusa com 400 CAMPO_REFERENCIADO; aqui o usuário descobre sem perder o trabalho).
   */
  const handleRemoveField = async (
    field: FormularioCampoView,
    setFieldValue: (campo: string, valor: unknown) => void
  ) => {
    const dependentes = camposQueDependemDe(
      formik.values.campos.filter((candidato) => candidato.ordem !== field.ordem),
      field.codigo
    )
    if (dependentes.length > 0) {
      const confirmado = await showConfirmAlert({
        text: t('formularios.construtor.campo.referenciado', { campos: dependentes.join(', ') }),
      })
      if (!confirmado) return
    }
    setDeletedFields((prev) => (field.id ? [...prev, field.id] : [...prev]))
    setFieldValue(
      'campos',
      formik.values.campos.filter((f) => f.ordem !== field.ordem)
    )
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const items = Array.from(formik.values.campos)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)

    const reordered = items.map((item, index) => ({
      ...item,
      ordem: index + 1,
    }))

    setOrderHasChanged(true)
    formik.setFieldValue('campos', reordered)
  }

  const handlePreview = () =>
    showModal({
      title: `${values.nome}`,
      content: <FormularioPreview campos={values.campos} catalogo={catalogo} />,
    })

  const handleDuplicar = async () => {
    if (!form?.id) return
    const confirmado = await showConfirmAlert({
      text: t('formularios.bloqueio.duplicar.confirmacao', { nome: form.nome }),
    })
    if (!confirmado) return
    try {
      setDuplicando(true)
      const copia = await duplicarFormulario(form.id)
      navigate(
        `/apps/formularios/editar?id=${copia.id}${
          copia.empresa ? `&empresaId=${copia.empresa.id}` : ''
        }`
      )
    } catch (error) {
      showError(error)
    } finally {
      setDuplicando(false)
    }
  }

  const formik = useFormik<FormularioView>({
    enableReinitialize: true,
    initialValues,
    validationSchema: form?.id ? updateValidationSchema : createValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      setAvisosServidor({})
      try {
        values.campos.forEach((field, index) => {
          field.ordem = index + 1
        })

        let criado: FormularioView | undefined
        if (values.id) {
          const updateForm: FormularioDtoUpdate = {
            ativo: values.ativo,
            nome: values.nome,
            id: values.id,
            descricao: values.descricao,
            assinar: values.assinar,
            assinarPaciente: values.assinarPaciente,
          }
          isAdmin
            ? await updateFormularioOfEmpresa(Number(empresaId), updateForm)
            : await updateFormulario(updateForm)

          let criouCampo = false
          for (const campo of values.campos) {
            if (campo.id) continue
            const createdField = isAdmin
              ? await createCampoOfEmpresa(paraDtoCampoCreate(campo), values.id)
              : await createCampo(paraDtoCampoCreate(campo), values.id)
            campo.id = createdField.id
            campo.codigo = createdField.codigo ?? campo.codigo
            criouCampo = true
          }

          if (deletedFields.length > 0)
            isAdmin ? await deleteCamposOfEmpresa(deletedFields) : await deleteCampos(deletedFields)

          // Reordenação em lote: uma chamada só, validada pelo backend sobre o estado final.
          if (orderHasChanged || criouCampo || deletedFields.length > 0) {
            const idsCampos = values.campos
              .map((campo) => Number(campo.id))
              .filter((id) => Number.isFinite(id))
            if (idsCampos.length > 0) {
              isAdmin
                ? await reordenarCamposOfEmpresa(values.id, idsCampos)
                : await reordenarCampos(values.id, idsCampos)
            }
          }
        } else {
          const campos: FormularioDtoCampoCreate[] = values.campos.map(paraDtoCampoCreate)

          const formUpdated: FormularioDtoCreate = { ...values, campos }

          criado = isAdmin
            ? await createFormularioOfEmpresa(Number(empresaId), formUpdated)
            : await createFormulario(formUpdated)
        }

        const destino = await showChoiceAlert({
          title: t(`formularios.salvar.sucesso.${values.id ? 'alterado' : 'criado'}`),
          icon: 'success',
          choices: [
            { value: 'edicao', text: t('formularios.salvar.continuarEditando') },
            { value: 'listagem', text: t('formularios.salvar.voltarListagem') },
          ],
        })
        if (destino === 'listagem') {
          navigate('/apps/formularios')
        } else if (criado?.id) {
          // Formulário novo: a edição continua na rota dele, já com o id gerado.
          const idEmpresa = criado.empresa?.id ?? (empresaId || undefined)
          navigate(
            `/apps/formularios/editar?id=${criado.id}${idEmpresa ? `&empresaId=${idEmpresa}` : ''}`,
            { replace: true }
          )
        } else {
          onRecarregar?.()
        }
      } catch (error) {
        const estruturado = extrairErrosFormulario(error)
        if (estruturado?.code === CODIGO_FORMULARIO_BLOQUEADO) {
          await handleBloqueado(estruturado.message)
          return
        }
        const erros = estruturado?.erros ?? []
        const ordemInvalida = erros.some((erro) => erro.codigo === 'ORDEM_INVALIDA')
        if (ordemInvalida) {
          showError(error)
          onRecarregar?.()
          return
        }
        if (erros.length > 0) {
          const avisos: Record<string, string> = {}
          erros.forEach((erro) => {
            if (erro.codigoCampo && erro.mensagem) avisos[erro.codigoCampo] = erro.mensagem
          })
          setAvisosServidor(avisos)
          if (Object.keys(avisos).length === 0) showError(error)
          else
            await showAlert({
              title: t('common.error'),
              text: estruturado?.message ?? '',
              icon: 'error',
            })
          return
        }
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { values, setFieldValue, errors, touched } = formik

  /** Avisos locais: condição que passou a apontar para um campo posterior/inexistente. */
  const avisosLocais = useMemo(() => {
    const esquema = values.campos.map(paraCampoEsquema).filter((campo) => campo.codigo !== '')
    const avisos: Record<number, string> = {}
    values.campos.forEach((campo, indice) => {
      if (!campo.condicaoExibicao) return
      const problemas = validarCondicaoLocal(
        campo.condicaoExibicao,
        esquemaParaExibicao(esquema, paraCampoEsquema(campo, indice), catalogo)
      ).filter((erro) => erro.codigo === 'CAMPO_POSTERIOR' || erro.codigo === 'CAMPO_INEXISTENTE')
      if (problemas.length > 0) avisos[campo.ordem] = t('formularios.condicao.aviso.reordenado')
    })
    return avisos
  }, [values.campos, catalogo, t])

  const temAviso = Object.keys(avisosLocais).length > 0
  const edicaoBloqueada = somenteLeitura || formik.isSubmitting

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='container py-4'>
        {somenteLeitura && (
          <div className='formulario-bloqueio-aviso' role='status'>
            <i
              className={`bi ${IconClass.Lock} formulario-bloqueio-aviso__icone`}
              aria-hidden='true'
            />
            <div className='formulario-bloqueio-aviso__corpo'>
              <span className='formulario-bloqueio-aviso__titulo'>
                {t('formularios.bloqueio.titulo')}
              </span>
              {form?.motivoBloqueio && (
                <span className='formulario-bloqueio-aviso__motivo'>{form.motivoBloqueio}</span>
              )}
            </div>
            <Button
              type={ButtonType.Button}
              className='btn-primary btn-sm'
              onClick={handleDuplicar}
              disabled={duplicando}
            >
              {duplicando ? <LoadingIndicator /> : t('formularios.bloqueio.duplicar')}
            </Button>
          </div>
        )}
        <div className='row'>
          <div className='col-md-8 p-2'>
            <div className='card'>
              <div className='card-header px-4'>
                <div className='d-flex flex-column justify-content-center'>
                  <h4 className='m-0'>Detalhes do formulário</h4>
                  <span className='text-muted fs-7'>
                    Nome e descrição exibidos para quem for preencher
                  </span>
                </div>
              </div>
              <div className='card-body p-4'>
                <FormInput
                  formik={formik}
                  name='nome'
                  label='Nome do formulário'
                  placeholder='Digite o nome do formulário'
                  className='mb-3'
                  isDisabled={edicaoBloqueada}
                />
                <FormInput
                  formik={formik}
                  name='descricao'
                  label='Descrição'
                  placeholder='Digite a descrição'
                  className='mb-0'
                  isDisabled={edicaoBloqueada}
                />
              </div>
            </div>

            <div className='card'>
              <div className='card-header px-4'>
                <div className='d-flex flex-column justify-content-center'>
                  <h4 className='m-0'>Configurações</h4>
                  <span className='text-muted fs-7'>Regras de assinatura deste formulário</span>
                </div>
              </div>
              <div className='card-body p-4'>
                <div className='formulario-config-lista'>
                  <div className='formulario-config-opcao'>
                    <IconChip icon={IconClass.Pen} />
                    <span className='formulario-config-opcao__corpo'>
                      <span className='formulario-config-opcao__titulo'>
                        Assinatura obrigatória
                      </span>
                      <span className='formulario-config-opcao__descricao'>
                        Exige assinatura para concluir o formulário
                      </span>
                    </span>
                    <FormToggle
                      formik={formik}
                      name='assinar'
                      isDisabled={edicaoBloqueada}
                      className='m-0'
                    />
                  </div>
                  <div className='formulario-config-opcao'>
                    <IconChip icon={IconClass.PersonCheck} />
                    <span className='formulario-config-opcao__corpo'>
                      <span className='formulario-config-opcao__titulo'>
                        Assinatura do paciente
                      </span>
                      <span className='formulario-config-opcao__descricao'>
                        Exibe o campo de assinatura do paciente na avaliação
                      </span>
                    </span>
                    <FormToggle
                      formik={formik}
                      name='assinarPaciente'
                      isDisabled={edicaoBloqueada}
                      className='m-0'
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='card'>
              <div className='card-header px-4 d-flex align-items-center justify-content-between'>
                <div className='d-flex flex-column justify-content-center'>
                  <h4 className='m-0'>Perguntas</h4>
                  <span className='text-muted fs-7'>Arraste para reordenar</span>
                </div>
                <span className='badge badge-light-primary fs-7'>{values.campos.length}</span>
              </div>
              <div className='card-body p-4'>
                {errors.campos && touched.campos && typeof errors.campos === 'string' && (
                  <div className='alert alert-danger' role='alert'>
                    {errors.campos}
                  </div>
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId='droppable'>
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className='pergunta-lista'
                      >
                        {values.campos.length === 0 && (
                          <div className='pergunta-vazio'>
                            <i className={`bi ${IconClass.UiChecksGrid}`} />
                            <span className='fw-semibold'>Nenhuma pergunta adicionada ainda</span>
                            <span className='fs-7'>
                              Escolha um tipo de campo no painel ao lado para começar.
                            </span>
                          </div>
                        )}
                        {values.campos.map((field, index) => (
                          <Draggable
                            key={String(field.id ?? `novo-${field.ordem}`)}
                            draggableId={String(field.id ?? `novo-${field.ordem}`)}
                            index={index}
                            isDragDisabled={edicaoBloqueada}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`pergunta-card ${
                                  snapshot.isDragging ? 'pergunta-card--arrastando' : ''
                                }`}
                              >
                                <span className='pergunta-card__handle' aria-hidden='true'>
                                  <i className={`bi ${IconClass.GripVertical}`} />
                                </span>
                                <span className='pergunta-card__numero'>{index + 1}</span>
                                <div className='pergunta-card__corpo'>
                                  <div className='pergunta-card__cabecalho'>
                                    <Label className='flex-grow-1' size={FontSize.Normal}>
                                      {field.rotulo || 'Pergunta sem título'}
                                    </Label>
                                    <code className='pergunta-card__codigo fs-8'>
                                      {field.codigo || t('formularios.construtor.codigo.gerado')}
                                    </code>
                                    {field.obrigatorio && (
                                      <span className='badge badge-light-danger fs-8'>
                                        Obrigatório
                                      </span>
                                    )}
                                    <div className='pergunta-card__acoes'>
                                      <ButtonIcon
                                        className='p-0'
                                        icon={IconPaths.Gear}
                                        iconSize={IconSize.Small}
                                        disabled={formik.isSubmitting}
                                        ariaLabel={`Configurar ${field.rotulo || 'campo'}`}
                                        onClick={() =>
                                          handleEditField(field, values.campos, setFieldValue)
                                        }
                                      />
                                      {!somenteLeitura && (
                                        <ButtonIcon
                                          className='p-0'
                                          icon={IconPaths.Trash}
                                          iconSize={IconSize.Small}
                                          disabled={formik.isSubmitting}
                                          ariaLabel={`Excluir ${field.rotulo || 'campo'}`}
                                          onClick={() => handleRemoveField(field, setFieldValue)}
                                        />
                                      )}
                                    </div>
                                  </div>
                                  {field.instrucoes && (
                                    <span className='pergunta-card__instrucoes'>
                                      {field.instrucoes}
                                    </span>
                                  )}
                                  {(avisosLocais[field.ordem] ||
                                    (field.codigo && avisosServidor[field.codigo])) && (
                                    <div
                                      className='alert alert-warning py-2 px-3 my-2'
                                      role='alert'
                                    >
                                      {avisosLocais[field.ordem] ||
                                        avisosServidor[field.codigo as string]}
                                    </div>
                                  )}
                                  <div className='w-100'>{renderField(field)}</div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>

            <div className='d-flex justify-content-end'>
              <Button
                className='btn-secondary me-3'
                onClick={handlePreview}
                disabled={formik.isSubmitting}
              >
                Pré-visualizar
              </Button>
              {!somenteLeitura && (
                <Button
                  type={ButtonType.Submit}
                  className='btn-primary'
                  disabled={formik.isSubmitting || temAviso}
                >
                  {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
                </Button>
              )}
            </div>
          </div>

          {!somenteLeitura && (
            <div className='col-md-4'>
              <div className='card position-sticky top-0'>
                <div className='card-header px-4'>
                  <div className='d-flex flex-column justify-content-center'>
                    <h4 className='m-0'>Adicionar campo</h4>
                    <span className='text-muted fs-7'>
                      Escolha o tipo de campo para incluir no formulário
                    </span>
                  </div>
                </div>
                <div className='card-body p-4'>
                  <div className='campo-tipo-lista'>
                    {formFields.map((field) => (
                      <button
                        key={field.type}
                        type='button'
                        className='campo-tipo-opcao'
                        disabled={formik.isSubmitting}
                        onClick={() => handleAddField(setFieldValue, values.campos, field.type)}
                      >
                        <IconChip icon={field.class} />
                        <span className='campo-tipo-opcao__corpo'>
                          <span className='campo-tipo-opcao__titulo'>{field.name}</span>
                          <span className='campo-tipo-opcao__descricao'>{field.descricao}</span>
                        </span>
                        <span className='campo-tipo-opcao__acao' aria-hidden='true'>
                          <i className={`bi ${IconClass.PlusLg}`} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
